import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAutoLogout } from '../../contexts/AutoLogoutContext';
import { Plus, LogOut, Settings, ChevronDown, User, BookOpen, Info, Download, Clock, LayoutDashboard } from 'lucide-react';
import EditProfileModal from '../Profile/EditProfileModal';
import { exportAPI } from '../../services/api';
import logoImage from '../../images/image.png';
import toast from 'react-hot-toast';
// Import ServiceDropdown from shared components (adjust path as needed)
// For now, we'll create a local version that can be replaced with shared component later

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { isWarningModalOpen, timeRemaining } = useAutoLogout();
  const navigate = useNavigate();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNewPublicationDropdownOpen, setIsNewPublicationDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const newPublicationDropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (newPublicationDropdownRef.current && !newPublicationDropdownRef.current.contains(event.target as Node)) {
        setIsNewPublicationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const handleDownloadManuscriptTracker = async () => {
    try {
      const blob = await exportAPI.downloadManuscriptTracker();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'manuscript_tracker.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Manuscript tracker downloaded successfully');
    } catch (error) {
      console.error('Error downloading manuscript tracker:', error);
      toast.error('Failed to download manuscript tracker');
    }
  };

  return (
    <nav className="bg-white shadow-lg border-b-2 border-green-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
          {/* Logo and Service Selector */}
          <div className="flex items-center space-x-3">
            <Link to="/publications" className="flex items-center space-x-3 text-green-600 hover:text-green-700">
              <img 
                src={logoImage} 
                alt="System Logo" 
                className="h-10 w-auto"
              />
            </Link>
            {/* Service label + link back to Data Engine (landing) */}
            <div className="relative" ref={useRef<HTMLDivElement>(null)}>
              <div className="flex items-center space-x-2">
                <button
                  className="flex items-center space-x-2 px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-default"
                  type="button"
                >
                  <span className="text-xl font-bold">Publication System</span>
                  <ChevronDown className="h-4 w-4 opacity-40" />
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 text-sm border border-green-600 text-green-700 rounded-full hover:bg-green-50 transition-colors"
                  title="Back to Data Engine"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  <span>Data Engine</span>
                </button>
              </div>
            </div>
          </div>

          {/* Navigation Items */}
          <div className="flex items-center space-x-4">
            {/* Publication Actions Dropdown - Available to all users */}
            <div className="relative" ref={newPublicationDropdownRef}>
              <button
                onClick={() => setIsNewPublicationDropdownOpen(!isNewPublicationDropdownOpen)}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Publications</span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {isNewPublicationDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {/* New Publication - Available to all users */}
                  <Link
                    to="/publications?new=1"
                    onClick={() => setIsNewPublicationDropdownOpen(false)}
                    className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    <span>New Publication</span>
                  </Link>
                  
                  {/* Download Manuscript Tracker - Available to all users */}
                  <button
                    onClick={() => {
                      handleDownloadManuscriptTracker();
                      setIsNewPublicationDropdownOpen(false);
                    }}
                    className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors w-full text-left"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download Manuscript Tracker</span>
                  </button>
                </div>
              )}
            </div>

            {user && (
              <>
                {/* User Menu Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                      {user ? getInitials(user.firstName, user.lastName) : 'U'}
                    </div>
                    <div className="hidden md:block">
                      <p className="text-sm font-medium text-gray-900">
                        {user ? `${user.firstName} ${user.lastName}` : 'User'}
                      </p>
                      <p className="text-xs text-gray-500 capitalize">{user?.role || 'user'}</p>
                    </div>
                    <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      <button
                        onClick={() => {
                          setIsProfileModalOpen(true);
                          setIsDropdownOpen(false);
                        }}
                        className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <User className="h-4 w-4" />
                        <span>Edit Profile</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          navigate('/savic-lab-publications');
                          setIsDropdownOpen(false);
                        }}
                        className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <BookOpen className="h-4 w-4" />
                        <span>Savic Lab Publication</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          navigate('/about');
                          setIsDropdownOpen(false);
                        }}
                        className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <Info className="h-4 w-4" />
                        <span>About</span>
                      </button>
                      
                      <div className="border-t border-gray-200 my-1"></div>
                      
                      {/* Admin Panel Link */}
                      {user && (user.role === 'admin' || user.role === 'superadmin') ? (
                        <Link
                          to="/admin"
                          onClick={() => setIsDropdownOpen(false)}
                          className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <Settings className="h-4 w-4" />
                          <span>Admin Panel</span>
                        </Link>
                      ) : null}
                      
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          navigate('/');
                        }}
                        className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <LayoutDashboard className="h-4 w-4" />
                        <span>Back to Data Engine</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsDropdownOpen(false);
                        }}
                        className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Auto-logout Activity Indicator */}
                {isWarningModalOpen && (
                  <div className="flex items-center space-x-2 bg-amber-100 border border-amber-300 rounded-lg px-3 py-2">
                    <Clock className="h-4 w-4 text-amber-600 animate-pulse" />
                    <span className="text-sm text-amber-800 font-medium">
                      Auto-logout in {Math.ceil(timeRemaining / 1000)}s
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <EditProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />
    </nav>
  );
};

export default Navbar;
